import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { request } from 'utils/request';
import _ from 'lodash';
import { planSelector } from 'domain/Options/selectors';
import { hasChangesSelector } from 'domain/Options/selectors/parameters';
import { LOADING_DATA } from 'domain/Options/constants';
import { loadData, planLoaded } from 'domain/Options/actions';
import { kitchenIslandMeasurementsSelector } from 'domain/Options/selectors/measurements';
import { overhangSideMeasurementsSelector } from 'domain/Options/selectors/overhangMeasurements';
import serializePlan from 'domain/Options/utils/serializePlan';
import { loadingLanguage, loadingUpptacka, languageLoaded, loadingError, commonLoaded, loadingApplicationSettings, loadingTranslations, loadingLocale, loadingDataProviderSettings, commonGlobalLoaded, loadingProductService } from './actions';
import { CHANGE_LANGUAGE } from './constants';
import { freestandingHobsSelector } from '../Options/selectors/operations';
import '@formatjs/intl-pluralrules/polyfill';
import { ProductService, SettingsService, TranslationsService } from "@inter-ikea-kompis/services";
import { DexfSettingsEnvironmentEnum, DexfSettingsFilterSegmentEnum } from '@inter-ikea-kompis/enums/';
import { upptackaEnabled } from 'app';
import { ipexGalleryEnabled } from 'app';

const ADDON_APP_PATH = "/addon-app";
const COMMON = 'common';
const PROXY_URL = "https://kompis-proxy.azurewebsites.net";
const CWCALC_PATH = `${ADDON_APP_PATH}/cwcalc/translations`;
const KOMPIS_PATH = `${ADDON_APP_PATH}/translations/kompis`;
const EN_XZ = "en-XZ.json";
const useProxy = location.host.startsWith("localhost");
const isRunningInCTEF = window.location.href.includes('cte');
const isRunningInPROD = window.location.href.includes('ikea.com');

function translationTool2react(type, data) {
  return _.flatMap(data, (namespace, namespaceKey) =>
    _.map(namespace, (value, key) => [`${type}.${namespaceKey}.${key}`, value]));
}

/**
 * Effect to handle loadgs dashboard
 */
export function* loadLanguage({ country, language }) {
  if(country === 'lv' && language === 'et') {
    language = 'lv';
  }
  yield put(loadingLanguage(true));
  if(!isRunningInCTEF){
    // yield put(loadingCookieSettings(country, language));
    yield put(loadingDataProviderSettings(country, language));
  }
 
  yield put(loadingLocale(country, language));
  
  try {
    let localizationCWCALC = `${CWCALC_PATH}/${language}-${country.toUpperCase()}.json`;
    let localizationCOMMON = `${CWCALC_PATH}/${EN_XZ}`;
    let localizationGlobalCOMMON = `${KOMPIS_PATH}/${EN_XZ}`;

    if (useProxy) {
      localizationCWCALC = `${PROXY_URL}${localizationCWCALC}`;
      localizationCOMMON = `${PROXY_URL}${localizationCOMMON}`;
      localizationGlobalCOMMON = `${PROXY_URL}${localizationGlobalCOMMON}`;
    }

    let [cwt, common, globalCommon] = yield all([
      call(request, localizationCWCALC),
      call(request, localizationCOMMON),
      call(request, localizationGlobalCOMMON),
    ]);

    let messages = translationTool2react(COMMON, common);
    let messagesGlobalCommon = translationTool2react(COMMON, globalCommon);
    const COUNTDOWN_BANNER_GALLERY_BUTTON_exists = messages.find((key) => {
      if(key[0] === 'common.kompis.countdownBannerGalleryButton'){
        return true;
      }
    });
    const COUNTDOWN_BANNER_HEADER_exists = messages.find((key) => {
      if(key[0] === 'common.kompis.countdownBannerHeader'){
        return true;
      }
    });
    const CONTINUE_exists = messages.find((key) => {
      if(key[0] === 'common.kompis.buttonContinue'){
        return true;
      }
    });
    messagesGlobalCommon.map((translation) => {
      if(!COUNTDOWN_BANNER_GALLERY_BUTTON_exists){
        if(translation[0] === 'common.kompis.countdownBannerGalleryButton'){
          messages.push(JSON.parse(JSON.stringify(translation)));
        }
      }
      if(!COUNTDOWN_BANNER_HEADER_exists){
        if(translation[0] === 'common.kompis.countdownBannerHeader') {
          messages.push(JSON.parse(JSON.stringify(translation)));
        }
      }
      if(!CONTINUE_exists){
        if(translation[0] === 'common.kompis.buttonContinue') {
          messages.push(JSON.parse(JSON.stringify(translation)));
        }
      }
    });

    // Add cwt messages
    messages = _.fromPairs(messages.concat(translationTool2react('cwt', cwt)));

    const cwcalcservice = new SettingsService({
      dexfApiKey: isRunningInPROD ? 'fa5412c4-4d29-4752-bbdb-8630ecc26af4' : 'fa5412c4-4d29-4752-bbdb-8630ecc26af4',
      dexfEnvironment: isRunningInPROD ? DexfSettingsEnvironmentEnum.production : DexfSettingsEnvironmentEnum.production,
      dexfApplicationId: 'cwcalc',
      locale:`${language}-${(country).toUpperCase()}`,
    });
    const dexfkompis = new SettingsService({
      dexfApiKey: isRunningInPROD ? '829e294e-1e25-4f81-975e-ad53ac4a3682' : '829e294e-1e25-4f81-975e-ad53ac4a3682',
      dexfEnvironment: isRunningInPROD ? DexfSettingsEnvironmentEnum.production : DexfSettingsEnvironmentEnum.production,
      dexfApplicationId: 'kompis',
      locale: 'sv-SE',
    });

    const translationEndpoints = useProxy ? [
      `${PROXY_URL}/addon-app/translations/kompis/en-XZ.json`,
      `${PROXY_URL}/addon-app/translations/kompis/{language}-{country}.json`,
      `${PROXY_URL}/addon-app/cwcalc/translations/en-XZ.json`,
      `${PROXY_URL}/addon-app/cwcalc/translations/{language}-{country}.json`
    ] : [
      '/addon-app/translations/kompis/en-XZ.json',
      '/addon-app/translations/kompis/{language}-{country}.json',
      '/addon-app/cwcalc/translations/en-XZ.json',
      '/addon-app/cwcalc/translations/{language}-{country}.json'
    ];

    const myTranslationService = new TranslationsService({
      // Locale in IETF BCP47 standard format
      locale: `${language}-${(country).toUpperCase()}`,
      translationPrefixApplication: 'CWCALC',
      translationEndpoints: translationEndpoints
    })
    const translationsService = yield call(loadTranslationsService, myTranslationService);
    
    const updatedMessageswithDEXFSettings = yield call(loadDexfSettings, cwcalcservice, dexfkompis, messages);
    const applicationSettings = yield call(loadApplicationSettings, cwcalcservice);
    yield put(loadingApplicationSettings(applicationSettings));

    const productService = new ProductService({
      dexfApiKey: isRunningInPROD ? 'fa5412c4-4d29-4752-bbdb-8630ecc26af4' : 'fa5412c4-4d29-4752-bbdb-8630ecc26af4',
      settings: applicationSettings,
      locale:`${language}-${(country).toUpperCase()}`,
    });
    yield put(loadingProductService(productService));

    yield put(loadingTranslations(translationsService));
    yield put(commonLoaded(updatedMessageswithDEXFSettings));
    yield put(commonGlobalLoaded(updatedMessageswithDEXFSettings, globalCommon, common));

    yield put(languageLoaded(country, language, updatedMessageswithDEXFSettings));
    yield put(loadingLanguage(false));

    if(upptackaEnabled || ipexGalleryEnabled){
      yield put(loadingUpptacka());
    }
  } catch (error) {
    yield put(loadingError(error));
  }
}

async function loadApplicationSettings(cwcalcservice) {
  try {
    let filterSegment = isRunningInPROD ? DexfSettingsFilterSegmentEnum.production : DexfSettingsFilterSegmentEnum.production;
    const cwcalcApplicationSettings = await cwcalcservice.getApplicationSettings(filterSegment);
    return cwcalcApplicationSettings;
  } catch (error) {
    console.error("While loading Application settings, got this error: ", error); 
  }
}

async function loadTranslationsService(myTranslationService) {
  try {
    const translationsService = await myTranslationService.getTranslations();
    return translationsService;
  } catch (error) {
    console.error("While loading translations settings, got this error: ", error); 
  }
}


async function loadDexfSettings(cwcalcservice, dexfkompis, cwt) {
  try {
    let filterSegment = isRunningInPROD ? DexfSettingsFilterSegmentEnum.production : DexfSettingsFilterSegmentEnum.development;
    const configurationSettingsCWCALC = await cwcalcservice.getApplicationSettings(filterSegment);
    let flattenedSettingsCWCALC = flattenObject(configurationSettingsCWCALC);

    // setting all keys to translations, instead of app, to keep consistency with the rest of the translations
    flattenedSettingsCWCALC = Object.entries(flattenedSettingsCWCALC).reduce((acc, [key, value]) => {
      let newKey = key.replace('.app.', '.translations.');
      acc[newKey] = value;
      return acc;
    }, {});

    const configurationSettingsKompis = await dexfkompis.getApplicationSettings(DexfSettingsFilterSegmentEnum.production);
    const result = { ...cwt, ...flattenedSettingsCWCALC, ...configurationSettingsKompis }; //combines the two and replaces cwt´s key-value pairs from flattenedSettingsCWCALC
    return result;
  } catch (error) {
    console.error("While loading DEXF settings, got this error: ", error); 
  }
}

const flattenObject = (obj, prefix = 'cwt.') => {
  let flattened = {};
  for (let key in obj) {
    if (typeof obj[key] === 'object') {
      Object.assign(flattened, flattenObject(obj[key], `${prefix}${key}.`));
    } else {
      flattened[`${prefix}${key}`] = obj[key];
    }
  }
  return flattened;
};


export function* loadLanguageAndRestorePlan({ country, language }) {
  yield call(loadLanguage, { country, language });

  const plan = yield select(planSelector);
  const kitchenIslandMeasurements = yield select(kitchenIslandMeasurementsSelector);
  const getOverhangMeasurements = yield select(overhangSideMeasurementsSelector);
  const freestandingHobMeasurements = yield select(freestandingHobsSelector);
  const overhangMeasurements = getOverhangMeasurements(plan.getIn(['overhangSide', 'value']));
  const serializedPlan = yield call(serializePlan, plan, { kitchenIslandMeasurements, overhangMeasurements, freestandingHobMeasurements });
  const hasChanges = yield select(hasChangesSelector);

  yield put(loadData(country, language));

  let loading = true;
  while (loading) {
    const action = yield take(LOADING_DATA);
    loading = action.loading;
  }

  yield put(planLoaded(serializedPlan, hasChanges, undefined, false));
}

export function* loadLanguageFlow() {
  // Initial load
  const action = yield take(CHANGE_LANGUAGE);
  yield call(loadLanguage, action);
  yield put(loadData(action.country, action.language));

  // Language changes
  yield takeLatest(CHANGE_LANGUAGE, loadLanguageAndRestorePlan);
}

export const importModule = (locale) => import(`@formatjs/intl-pluralrules/locale-data/${locale}`);

export default loadLanguageFlow;
